/**
 * Pure utility functions for the chart component.
 * Kept in a separate file so that react-refresh/only-export-components
 * does not warn about Chart.tsx exporting non-component symbols.
 */

import type { CandlestickData, Time } from 'lightweight-charts';

/**
 * Pure tick-to-candle math — no side effects, no refs.
 *
 * Given the current last candle, a tick, and the timeframe duration in
 * seconds, returns:
 *   - An updated candle (same time slot) if the tick falls in the same candle.
 *   - A new candle (next time slot) if the tick opens a new bar.
 *   - `null` if the tick is older than the last candle (stale — discard).
 *
 * Exported so it can be unit-tested directly without mounting the chart.
 */
export function computeTickCandle(
  lastCandle: CandlestickData<Time>,
  tick: { price: number; timestamp: number },
  timeframeSeconds: number,
): CandlestickData<Time> | null {
  const tickTime = Math.floor(tick.timestamp / 1000);
  const candleTime = Math.floor(tickTime / timeframeSeconds) * timeframeSeconds;
  const lastCandleTime = lastCandle.time as number;

  if (candleTime === lastCandleTime) {
    return {
      time: lastCandleTime as Time,
      open: lastCandle.open,
      high: Math.max(lastCandle.high, tick.price),
      low: Math.min(lastCandle.low, tick.price),
      close: tick.price,
    };
  }

  if (candleTime > lastCandleTime) {
    return {
      time: candleTime as Time,
      open: tick.price,
      high: tick.price,
      low: tick.price,
      close: tick.price,
    };
  }

  // Tick is older than the last candle — discard.
  return null;
}

/**
 * Detect weekend / closure gaps in candlestick data.
 * Returns an array of unix-second timestamps that mark the *start* of each
 * gap (i.e. the time of the first candle after the gap).
 * A gap is defined as any interval between consecutive bars > timeframeSeconds * 2,
 * with a minimum of 1 hour to ignore minor data holes on short timeframes.
 *
 * TODO: wire up to the chart renderer (e.g. ChartNeonOverlay) to draw
 * visible vertical gap markers — currently exported and tested but not
 * consumed in production.
 */
export function detectWeekendGaps(
  data: ReadonlyArray<{ time: number }>,
  timeframeSeconds: number,
): number[] {
  const gaps: number[] = [];
  // Require at least a 1-hour gap to avoid flagging minor data holes on short timeframes.
  const threshold = Math.max(timeframeSeconds * 2, 3600);
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1].time;
    const curr = data[i].time;
    if (curr - prev > threshold) {
      gaps.push(curr);
    }
  }
  return gaps;
}

/**
 * Format a unix timestamp (seconds) as a human-readable UTC string.
 * e.g. "Mon 12:00 UTC"
 */
export function formatUtcTime(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const day = dayNames[date.getUTCDay()];
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day} ${hours}:${minutes} UTC`;
}
